/**
 * WANN PROFIL UND PROJEKTLISTE NEU GELADEN WERDEN — und wann gerade nicht.
 *
 * ─── Der Anlass ─────────────────────────────────────────────────────────────
 * auth-js (2.67) meldet bei JEDEM Wechsel des Tabs von verborgen nach
 * sichtbar ein `SIGNED_IN` (`_onVisibilityChanged` → `_recoverAndRefresh`),
 * kurz vor Ablauf des Tokens stattdessen `TOKEN_REFRESHED`. Die Rueckkehr aus
 * der Kamera (`capture="environment"`) ist genau so ein Wechsel. Bis 10/2026
 * hing an jedem dieser Ereignisse `fetchProfile` und damit die VOLLE
 * Projektliste (607 Projekte, rund 1,4 MB gzip je Laden). Ein Mitarbeiter
 * kam im September auf etwa 1.550 Listen-Loads und gut 2 GB mobile Daten —
 * fast alles fuer Daten, die er schon hatte.
 *
 * ─── Die Regel ──────────────────────────────────────────────────────────────
 *  - Profil und Liste laden, wenn die Sitzung einem ANDEREN Nutzer gehoert als
 *    dem, fuer den schon geladen ist (oder gerade geladen wird): erster Start,
 *    Anmeldung nach Abmeldung, Benutzerwechsel im selben Tab.
 *  - Derselbe Nutzer kommt zurueck (`SIGNED_IN`, `TOKEN_REFRESHED`,
 *    `INITIAL_SESSION`, `PASSWORD_RECOVERY`): nichts laden. Das frische Token
 *    haelt supabase-js selbst; App.tsx bekommt die neue Sitzung trotzdem,
 *    haengt seine Effekte aber an die Nutzerkennung, nicht an das Objekt.
 *  - `USER_UPDATED`: nur das Profil, nicht die Liste.
 *
 * Damit die Liste trotzdem nicht veraltet, gibt es ein bewusstes Nachholen
 * (`erzeugeNachholer`): nach mindestens zehn Minuten Abwesenheit, beim
 * `online` des Browsers und wenn der Realtime-Kanal nach einem Abriss wieder
 * steht — alle zusammen hoechstens einmal je zehn Minuten. Ein gebremster
 * Bedarf wird gemerkt und zum ersten erlaubten Zeitpunkt eingeloest.
 *
 * Rein und ohne DOM: App.tsx reicht Zeit, Sichtbarkeit und den Stand herein.
 */

/** Was ein Auth-Ereignis laden soll. */
export type AuthLadeArt = 'voll' | 'profil' | 'keins';

export interface AuthEreignisLage {
    /** Das Ereignis aus `onAuthStateChange` (oder `'INITIAL_SESSION'` fuer `getSession`). */
    event: string;
    /** Nutzer der gemeldeten Sitzung; `null` ohne Sitzung. */
    sitzungNutzerId: string | null;
    /** Fuer wen Profil und Liste bereits geladen sind oder gerade laden. */
    geladenFuer: string | null;
}

export const entscheideAuthLaden = ({ event, sitzungNutzerId, geladenFuer }: AuthEreignisLage): AuthLadeArt => {
    // Ohne Sitzung entscheidet der Abmeldezweig in App.tsx (sessionRevocation).
    if (!sitzungNutzerId) return 'keins';
    // Erster Start, neue Anmeldung, anderer Nutzer: alles laden. Auch ein
    // fehlgeschlagener erster Versuch landet hier — App.tsx setzt
    // `geladenFuer` dann wieder zurueck.
    if (sitzungNutzerId !== geladenFuer) return 'voll';
    if (event === 'USER_UPDATED') return 'profil';
    return 'keins';
};

// ═══════════════════════════════════════════════════════════════════════════
// NACHHOLEN — die Liste wird nicht alt, auch ohne Laden bei jeder Rueckkehr
// ═══════════════════════════════════════════════════════════════════════════

/** Hoechstens ein Nachholen (voller Listen-Load) je so viel Zeit — ueber alle Anlaesse. */
export const NACHHOL_TAKT_MS = 10 * 60 * 1000;

/** So lange muss die App verborgen gewesen sein, damit die Rueckkehr nachholt. */
export const NACHHOL_ABWESENHEIT_MS = 10 * 60 * 1000;

/** Nach einem gescheiterten Nachholen (kein Netz) erst so viel spaeter wieder. */
export const NACHHOL_WIEDERHOLUNG_MS = 60 * 1000;

/**
 * Warum nachgeholt werden soll.
 *  - `sichtbar`: der Tab ist nach mindestens zehn Minuten wieder sichtbar.
 *  - `online`:   der Browser meldet wieder Netz.
 *  - `kanal`:    der Realtime-Kanal steht nach einem Abriss wieder — was in
 *                der Luecke geschah, kam nie an.
 *  - `stau`:     Realtime kann nicht einzeln nachziehen — zu viele Projekte
 *                vorgemerkt (realtimeProjectSync) oder ein Ereignis ohne
 *                Kennung. Ein Voll-Load ist dann der kuerzere Weg.
 *
 * Die Rueckkehr aus der Kamera ist KEIN Anlass. Reisst dabei aber wirklich
 * die Verbindung ab (`online`, `kanal`), kann ein Ereignis verloren sein —
 * das wird nachgeholt, im selben Takt wie alles andere.
 */
export type NachholAnlass = 'sichtbar' | 'online' | 'kanal' | 'stau';

/** Die letzte Rueckkehr aus dem Hintergrund: wann, und wie lange die App weg war. */
export interface Rueckkehr {
    amMs: number;
    wegMs: number;
}

/** Begruendet die Rueckkehr ein Nachholen? Nur nach mindestens zehn Minuten weg. */
export const rueckkehrBrauchtNachholen = (rueckkehr: Rueckkehr | null): boolean =>
    rueckkehr !== null && rueckkehr.wegMs >= NACHHOL_ABWESENHEIT_MS;

export interface NachholLage {
    jetztMs: number;
    /** Beginn des letzten ERFOLGREICHEN Voll-Loads; `null` = noch keiner. */
    letzterVollLoadMs: number | null;
    /** Tab gerade verborgen? */
    versteckt: boolean;
    /** Laeuft schon ein Nachholen? */
    laeuft?: boolean;
}

/** Darf ein Nachholen JETZT laufen? */
export const darfJetztNachholen = ({ jetztMs, letzterVollLoadMs, versteckt, laeuft }: NachholLage): boolean => {
    // Im Hintergrund wird nichts geladen — es sieht ja niemand hin. Der
    // Bedarf bleibt stehen, bis der Tab wieder sichtbar ist.
    if (versteckt || laeuft) return false;
    // Noch nie erfolgreich geladen (offline gestartet, Abbruch): sofort —
    // sonst saesse der Mitarbeiter bis zu zehn Minuten vor einer leeren
    // Liste. So hat es sich bisher auch verhalten.
    if (letzterVollLoadMs === null) return true;
    // Der gemeinsame Takt. Gezaehlt wird nur ein ERFOLGREICHER Load.
    return jetztMs - letzterVollLoadMs >= NACHHOL_TAKT_MS;
};

/** Ab wann der Takt das naechste Nachholen erlaubt. */
export const naechstesNachholenAbMs = (letzterVollLoadMs: number | null): number =>
    (letzterVollLoadMs === null ? 0 : letzterVollLoadMs + NACHHOL_TAKT_MS);

/**
 * Die Rueckkehr aus dem Hintergrund festhalten. `verstecktSeitMs` ist der
 * Zeitpunkt des letzten Wechsels nach `hidden`; fehlt er (die App war nie
 * weg, oder der Wechsel kam vor dem Zuhoerer), gibt es keine Rueckkehr.
 */
export const bildeRueckkehr = (verstecktSeitMs: number | null, jetztMs: number): Rueckkehr | null =>
    (verstecktSeitMs === null ? null : { amMs: jetztMs, wegMs: Math.max(0, jetztMs - verstecktSeitMs) });

// ═══════════════════════════════════════════════════════════════════════════
// DER NACHHOLER — ein Bedarf geht nicht verloren, nur weil der Takt ihn bremst
// ═══════════════════════════════════════════════════════════════════════════
//
// Codex-Review 21.10.2026: Bremste der Takt ein Nachholen (letzter Load 10:00,
// Abriss um 10:03), war der Anlass weg — blieb der Tab danach sichtbar und
// die Verbindung stabil, blieb die Liste auch nach 10:10 alt. Deshalb merkt
// sich der Nachholer den Bedarf und loest ihn zum ersten erlaubten Zeitpunkt
// ein; verborgen wartet er auf das Sichtbarwerden. Erledigt ist der Bedarf
// erst durch einen Voll-Load, der NACH ihm begonnen hat — gleich, wer ihn
// ausgeloest hat.

export interface NachholerOptionen {
    istVersteckt: () => boolean;
    /** Beginn des letzten erfolgreichen Voll-Loads (App: `letzterVollLoadRef`). */
    letzterVollLoadMs: () => number | null;
    /** Das Nachholen selbst; liefert den Beginn des Loads, `null` bei einem Fehlschlag. */
    laden: () => Promise<number | null>;
    jetzt?: () => number;
    schedule?: (fn: () => void, ms: number) => unknown;
    cancel?: (handle: unknown) => void;
}

export interface Nachholer {
    /**
     * Einen Bedarf melden. Liefert den Lauf, wenn er JETZT startet, sonst
     * `null`. `merken = false`: jetzt oder gar nicht (der Sammler zieht dann
     * einzeln nach und braucht keinen spaeteren Voll-Load).
     */
    melde: (anlass: NachholAnlass, merken?: boolean) => Promise<number | null> | null;
    /** Der Tab ist wieder sichtbar; nach langer Abwesenheit ist das selbst ein Bedarf. */
    sichtbar: (rueckkehr: Rueckkehr | null) => Promise<number | null> | null;
    abbauen: () => void;
    /** Der offene Bedarf (fuer Tests und Protokoll). */
    offen: () => NachholAnlass | null;
}

export const erzeugeNachholer = (opts: NachholerOptionen): Nachholer => {
    const jetzt = opts.jetzt ?? (() => Date.now());
    const schedule = opts.schedule ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
    const cancel = opts.cancel ?? ((h: unknown) => clearTimeout(h as ReturnType<typeof setTimeout>));

    /** Der offene Bedarf — mit dem Zeitpunkt der JUENGSTEN Meldung. */
    let bedarf: { anlass: NachholAnlass; seitMs: number } | null = null;
    let laeuft = false;
    let wiederholenAbMs = 0;
    let timer: unknown = null;
    let timerAktiv = false;
    let abgebaut = false;

    const stoppen = () => {
        if (timerAktiv) cancel(timer);
        timer = null;
        timerAktiv = false;
    };

    /** Ein Voll-Load, der nach dem Bedarf begonnen hat, hat ihn erledigt. */
    const erledigtPruefen = () => {
        const letzter = opts.letzterVollLoadMs();
        if (bedarf && letzter !== null && letzter >= bedarf.seitMs) bedarf = null;
    };

    const planen = () => {
        stoppen();
        if (abgebaut || !bedarf || laeuft || opts.istVersteckt()) return;
        const ab = Math.max(naechstesNachholenAbMs(opts.letzterVollLoadMs()), wiederholenAbMs);
        timerAktiv = true;
        timer = schedule(() => {
            timerAktiv = false;
            timer = null;
            void versuchen();
        }, Math.max(0, ab - jetzt()));
    };

    const starten = (): Promise<number | null> | null => {
        const t = jetzt();
        if (abgebaut || t < wiederholenAbMs) return null;
        if (!darfJetztNachholen({ jetztMs: t, letzterVollLoadMs: opts.letzterVollLoadMs(), versteckt: opts.istVersteckt(), laeuft })) return null;
        laeuft = true;
        stoppen();
        return opts.laden().catch(() => null).then((beginn) => {
            laeuft = false;
            wiederholenAbMs = beginn === null ? jetzt() + NACHHOL_WIEDERHOLUNG_MS : 0;
            erledigtPruefen();
            planen();
            return beginn;
        });
    };

    const versuchen = (): Promise<number | null> | null => {
        if (abgebaut) return null;
        erledigtPruefen();
        if (!bedarf) return null;
        const lauf = starten();
        if (!lauf) planen();
        return lauf;
    };

    return {
        melde: (anlass, merken = true) => {
            if (abgebaut) return null;
            if (!merken) return starten();
            bedarf = { anlass, seitMs: jetzt() };
            return versuchen();
        },
        sichtbar: (rueckkehr) => {
            if (abgebaut) return null;
            if (rueckkehrBrauchtNachholen(rueckkehr)) bedarf = { anlass: 'sichtbar', seitMs: jetzt() };
            // Noch nie erfolgreich geladen (Erstladung gescheitert): auch die
            // kurze Rueckkehr versucht es — so wie bisher jede Rueckkehr
            // (Codex-Review 21.10.2026, Runde 2). Jeder erfolgreiche Load
            // erledigt diesen Bedarf (`seitMs` 0), auch einer, der schon lief;
            // der Abstand nach einem Fehlschlag (`wiederholenAbMs`) gilt weiter.
            else if (!bedarf && opts.letzterVollLoadMs() === null) bedarf = { anlass: 'sichtbar', seitMs: 0 };
            return versuchen();
        },
        abbauen: () => {
            abgebaut = true;
            stoppen();
            bedarf = null;
        },
        offen: () => bedarf?.anlass ?? null,
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// DIE SITZUNGSRUNDE — eine Antwort fuer A landet nie in der Sitzung von B
// ═══════════════════════════════════════════════════════════════════════════
//
// Hochgezaehlt bei jedem Wechsel des angemeldeten Nutzers (auch nach null).
// Wer laedt, gibt die Runde mit, FUER die er laedt, und schreibt nach jedem
// `await` nur, wenn sie noch gilt. Ausdruecklich mitgeben statt beim Aufruf
// abgreifen (Codex-Review 21.10.2026, Runde 2): ein Zeitgeber des alten
// Effekts kann zwischen Nutzerwechsel und Abbau noch feuern — griffe er die
// Runde erst beim Aufruf ab, bekaeme er die von B, und das Profil von A
// ueberschriebe den Zustand von B.

export interface SitzungsRunde {
    nutzerId: string | null;
    runde: number;
}

export const ERSTE_SITZUNGSRUNDE: SitzungsRunde = { nutzerId: null, runde: 0 };

/** Der Stand nach einem Auth-Ereignis: derselbe Nutzer behaelt die Runde, jeder Wechsel zaehlt weiter. */
export const naechsteSitzungsRunde = (stand: SitzungsRunde, nutzerId: string | null): SitzungsRunde =>
    (stand.nutzerId === nutzerId ? stand : { nutzerId, runde: stand.runde + 1 });

/** Gilt die Runde, fuer die geladen wurde, noch? */
export const rundeGilt = (erwartet: number, stand: SitzungsRunde): boolean => erwartet === stand.runde;
